import React, { useMemo, useRef, useState } from 'react'
import { Menu, MenuItem, Tooltip } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser'
import { Badge, Section } from '../BusinessDetailReport/BusinessDetailReport'
import styles from '../BusinessDetailReport/BusinessDetailReport.module.scss'
import { buildOwnerReport } from './ownerReportModel'
import PersonalInfo from './PersonalInfo'
import LinkedIdentities from './LinkedIdentities'
import CriminalRecords from './CriminalRecords'
import Bankruptcies from './Bankruptcies'
import LiensJudgments from './LiensJudgments'
import OwnerUccFilings from './OwnerUccFilings'
import BusinessTies from './BusinessTies'
import ProfessionalLicenses, { DeaRegistrations } from './ProfessionalLicenses'
import Property from './Property'
import Vehicles from './Vehicles'
import Addresses from './Addresses'
import People, { SexOffenses } from './People'
import Watchlists, { Sanctions } from './Watchlists'
import Accidents from './Accidents'
import OtherRecords from './OtherRecords'
import { withReportErrorBoundary } from '../BusinessDetailReport/ReportErrorBoundary'

const cx = (...names) => names.filter(Boolean).join(' ')
const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])

const deaRows = (person) => list(person?.ControlledSubstances?.ControlledSubstance).flatMap(cs => {
    const infos = list(cs?.ControlledSubstancesInfo?.ControlledSubstanceInfo)
    return infos.length ? infos.map(i => ({ ...i, RegistrationNumber: cs.RegistrationNumber })) : [{ RegistrationNumber: cs?.RegistrationNumber }]
})

function OwnerReport({ person }) {
    const model = useMemo(() => buildOwnerReport(person), [person])
    const idPrefix = useRef(`owner-report-${Math.random().toString(36).slice(2)}`).current
    const [openState, setOpenState] = useState({})
    const [moreAnchor, setMoreAnchor] = useState(null)

    if (!model.hasData) {
        return (
            <div className={styles.body}>
                <div className={styles.calmLine}>
                    No person data returned. Lexis found no record matching the name, date of birth and SSN on the application.
                    Check those three fields on the application, then refresh the report.
                </div>
            </div>
        )
    }

    const owner = person?.BestInfo || {}
    const { summary, tiles, sections, banner } = model
    const domId = id => `${idPrefix}-${id}`
    const isOpen = s => (openState[s.id] ?? s.open)
    const toggle = s => setOpenState(prev => ({ ...prev, [s.id]: !isOpen(s) }))
    const jumpTo = id => {
        setOpenState(prev => ({ ...prev, [id]: true }))
        setMoreAnchor(null)
        setTimeout(() => document.getElementById(domId(id))?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0)
    }
    const mainLinks = sections.filter(s => s.main || s.tone)
    const moreLinks = sections.filter(s => !(s.main || s.tone))

    const body = (id) => {
        switch (id) {
            case 'personal': return <PersonalInfo person={person} showSummary={false} />
            case 'linked': return <LinkedIdentities imposters={person?.Imposters?.Imposter} owner={owner} showSummary={false} />
            case 'watchlists': return <Watchlists hits={person?.GlobalWatchLists?.GlobalWatchList} owner={owner} showSummary={false} />
            case 'sanctions': return <Sanctions sanctions={person?.Sanctions?.Sanction} owner={owner} showSummary={false} />
            case 'sexOffender': return <SexOffenses person={person} />
            case 'criminal': return <CriminalRecords records={person?.CriminalRecords?.Criminal} owner={owner} showSummary={false} />
            case 'bankruptcies': return <Bankruptcies bankruptcies={person?.Bankruptcies?.Bankruptcy} owner={owner} showSummary={false} />
            case 'liens': return <LiensJudgments liens={person?.LiensJudgments?.LienJudgment} owner={owner} showSummary={false} />
            case 'ucc': return <OwnerUccFilings uccs={person?.UCCFilings?.UCCFiling} showSummary={false} />
            case 'ties': return <BusinessTies person={person} showSummary={false} />
            case 'licenses': return <ProfessionalLicenses licenses={person?.ProfessionalLicenses?.ProfessionalLicense} person={person} showSummary={false} />
            case 'dea': return <DeaRegistrations registrations={deaRows(person)} />
            case 'property': return <Property person={person} showSummary={false} />
            case 'vehicles': return <Vehicles person={person} showSummary={false} />
            case 'accidents': return <Accidents person={person} showSummary={false} />
            case 'addresses': return <Addresses addresses={person?.BpsReportAddresses?.BpsReportAddress} owner={owner} showSummary={false} />
            case 'relatives': return <People person={person} />
            case 'other': return <OtherRecords person={person} />
            default: return null
        }
    }

    return (
        <div className={styles.body}>
            <div className={styles.summary}>
                <div className={styles.identity}>
                    <h2 className={styles.companyName}>{summary.name}</h2>
                    {summary.lines.map(line => <div key={line} className={styles.summaryLine}>{line}</div>)}
                    {summary.badges.length > 0 && (
                        <div className={styles.summaryBadges}>
                            {summary.badges.map(b => <Badge key={b.label} tone={b.tone} dot>{b.label}</Badge>)}
                        </div>
                    )}
                </div>
                <div className={styles.tiles}>
                    {tiles.map(t => (
                        <Tooltip key={t.label} title={t.hint || ''} arrow placement='bottom' enterDelay={300}>
                            <button type='button' className={cx(styles.tile, styles.tileButton, t.flag && styles.tileFlag)} onClick={() => jumpTo(t.target || t.section)}>
                                <span className={styles.tileLabel}>{t.label}</span>
                                <span className={cx(styles.tileValue, typeof t.value !== 'number' && styles.tileValueText)}>
                                    {t.value}{t.unit && <span className={styles.tileUnit}> {t.unit}</span>}
                                </span>
                                <span className={styles.tileSub}>{t.sub}</span>
                            </button>
                        </Tooltip>
                    ))}
                </div>
            </div>

            <nav className={styles.links} aria-label='Report sections'>
                {mainLinks.map(s => (
                    <button key={s.id} type='button' className={styles.link} onClick={() => jumpTo(s.id)}>
                        <span>{s.linkTitle}</span>
                        {s.tone ? <Badge tone={s.tone}>{s.count}</Badge> : <span className={styles.linkCount}>{s.count}</span>}
                    </button>
                ))}
                {moreLinks.length > 0 && (
                    <>
                        <button type='button' className={cx(styles.link, styles.moreLink)} onClick={e => setMoreAnchor(e.currentTarget)}
                            aria-haspopup='menu' aria-expanded={Boolean(moreAnchor)}>
                            More <ExpandMoreIcon fontSize='small' />
                        </button>
                        <Menu anchorEl={moreAnchor} open={Boolean(moreAnchor)} onClose={() => setMoreAnchor(null)}
                            getContentAnchorEl={null} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
                            {moreLinks.map(s => (
                                <MenuItem key={s.id} onClick={() => jumpTo(s.id)}>{s.title}{s.count ? ` (${s.count})` : ''}</MenuItem>
                            ))}
                        </Menu>
                    </>
                )}
            </nav>

            {banner && (
                <div className={styles.allClear} role='status'>
                    <VerifiedUserIcon className={styles.allClearIcon} />
                    <div><strong>Nothing to flag.</strong> {banner.text}</div>
                </div>
            )}

            {sections.map(s => (
                <Section key={s.id} id={domId(s.id)} title={s.title} open={isOpen(s)} onToggle={() => toggle(s)}
                    summary={s.tone ? <Badge tone={s.tone} dot={s.tone === 'declined'}>{s.summary}</Badge> : <span className={styles.cardSummary}>{s.summary}</span>}>
                    {body(s.id)}
                </Section>
            ))}
        </div>
    )
}

export default withReportErrorBoundary(OwnerReport, 'person')
